import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Seo, BareLayout } from "@/components/Layout";
import Logo from "@/components/Logo";
import { Overline } from "@/components/Bits";
import api from "@/lib/api";
import { Check, X, ArrowRight, ArrowLeft, Award } from "lucide-react";

export default function LevelTest() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/level-test").then(({ data }) => setQuestions(data)); }, []);

  const q = questions[idx];
  const submit = async () => {
    setLoading(true);
    try { const { data } = await api.post("/level-test/submit", { answers }); setResult(data); }
    finally { setLoading(false); }
  };

  if (result) {
    const SKILL = { vocabulario: "Vocabulario", gramatica: "Gramática", comprension: "Comprensión" };
    return (
      <BareLayout>
        <Seo title="Resultado de la prueba de nivel" noindex />
        <div className="min-h-screen">
          <div className="border-b border-border p-4"><Logo /></div>
          <div className="mx-auto max-w-2xl px-4 py-14" data-testid="level-result">
            <Overline>Tu resultado orientativo</Overline>
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center"><Award className="text-primary" size={28} /></div>
              <div>
                <h1 className="font-display font-black text-4xl">{result.level}</h1>
                <p className="text-sm text-muted-foreground">{result.correct} de {result.total} respuestas correctas</p>
              </div>
            </div>
            <div className="mt-8 rounded-md bg-amber-500/10 p-4 text-sm text-muted-foreground">{result.disclaimer}</div>

            <div className="mt-8 grid gap-3">
              {Object.entries(result.by_skill).map(([s, v]) => (
                <div key={s}>
                  <div className="flex justify-between text-sm mb-1"><span className="font-medium">{SKILL[s] || s}</span><span className="text-muted-foreground">{v.c}/{v.t}</span></div>
                  <Progress value={v.t ? (v.c / v.t) * 100 : 0} />
                </div>
              ))}
            </div>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-lg border border-border p-5">
                <h3 className="font-display font-semibold mb-2">Fortalezas</h3>
                {result.strengths.length ? result.strengths.map((s) => <span key={s} className="inline-block mr-2 mb-2 text-sm px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">{SKILL[s] || s}</span>) : <p className="text-sm text-muted-foreground">Sigue practicando para consolidar tus puntos fuertes.</p>}
              </div>
              <div className="rounded-lg border border-border p-5">
                <h3 className="font-display font-semibold mb-2">Áreas de mejora</h3>
                {result.improve.length ? result.improve.map((s) => <span key={s} className="inline-block mr-2 mb-2 text-sm px-2 py-1 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400">{SKILL[s] || s}</span>) : <p className="text-sm text-muted-foreground">¡Buen equilibrio en todas las áreas!</p>}
              </div>
            </div>

            <div className="mt-8 rounded-lg border-2 border-primary p-6">
              <Overline>Ruta recomendada</Overline>
              <h3 className="font-display font-bold text-xl">Alemán esencial para empezar en Suiza</h3>
              <div className="mt-4 flex flex-wrap gap-3">
                <Link to="/cursos"><Button className="rounded-full">Empezar la ruta <ArrowRight size={16} className="ml-1" /></Button></Link>
                <Link to="/registro"><Button variant="outline" className="rounded-full">Crear cuenta gratis</Button></Link>
              </div>
            </div>
          </div>
        </div>
      </BareLayout>
    );
  }

  return (
    <BareLayout>
      <Seo title="Prueba de nivel de alemán" description="Descubre tu nivel de alemán orientativo (principiante, A1, A2 o B1) y recibe una ruta recomendada." noindex />
      <div className="min-h-screen flex flex-col">
        <div className="border-b border-border p-4 flex items-center justify-between">
          <Logo /><Link to="/" className="text-sm text-muted-foreground hover:text-foreground">Salir</Link>
        </div>
        <div className="flex-1 mx-auto w-full max-w-2xl px-4 py-10">
          {q ? (
            <>
              <Progress value={((idx) / questions.length) * 100} className="mb-8" />
              <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Pregunta {idx + 1} de {questions.length}</p>
              <h1 className="font-display font-bold text-2xl md:text-3xl mb-6" data-testid="test-question">{q.prompt}</h1>
              <div className="grid gap-3">
                {q.options.map((o) => {
                  const sel = answers[q.id] === o;
                  return (
                    <button key={o} onClick={() => setAnswers({ ...answers, [q.id]: o })}
                      data-testid={`test-option-${o}`}
                      className={`text-left px-4 py-3.5 rounded-md border text-sm font-medium transition-colors ${sel ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-secondary"}`}>
                      {o}
                    </button>
                  );
                })}
              </div>
              <div className="mt-8 flex justify-between">
                <Button variant="ghost" disabled={idx === 0} onClick={() => setIdx(idx - 1)}><ArrowLeft size={16} className="mr-1" />Anterior</Button>
                {idx < questions.length - 1 ? (
                  <Button disabled={!answers[q.id]} onClick={() => setIdx(idx + 1)} data-testid="test-next">Siguiente <ArrowRight size={16} className="ml-1" /></Button>
                ) : (
                  <Button disabled={!answers[q.id] || loading} onClick={submit} data-testid="test-submit">{loading ? "Calculando..." : "Ver resultado"}</Button>
                )}
              </div>
            </>
          ) : <p className="text-muted-foreground">Cargando prueba...</p>}
        </div>
      </div>
    </BareLayout>
  );
}
